import React, { useState, useContext, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Heading,
  Switch,
  RadioButton,
  Button,
  Link,
  Checkbox,
  Tag,
  ThemeStore,
} from "@foris/avocado-suite";
import { Icon } from "@foris/avocado-icons";
import cx from "classnames";
import CampusSelector from "./CampusSelector";
import CrnSelector from "./CrnSelector";
import SchoolSelector from "./SchoolSelector";
import DepartmentSelector from "./DepartmentSelector";
import SubjectSelector from "./SubjectSelector";
import { Context } from "../../context/GroupsManagerContext";
import { BooleanFilterId } from "../../context/types";
import { Types as FiltersSelectorsTypes } from "../../context/filtersSelectors.reducer";
import { Types } from "../../context/filters.reducer";
import stringifyFilters from "../../utils/stringifyFilters";
import CourseTypeSelector from "./CourseTypesSelector";
import PackageSelector from "./PackageSelector";
import { Package } from "@models/ISchema";
import { FiltersType, SelectableOption } from "../../models";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./filters.module.scss";

type RequestGroupsPayload = {
  page: number;
  avoidEditionsCleaning: boolean;
  usedFilter?: FiltersType;
};

interface IFiltersProps {
  requestGroups: (payload: RequestGroupsPayload) => void;
  isRestoringList?: boolean;
}

interface LocationState {
  filter?: FiltersType;
  data?: Package;
}

const selectorsAnimationProps = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.3, ease: "easeInOut" as const },
  className: styles.filtersCard_filters,
};

const Filters = (props: IFiltersProps) => {
  const { t } = useTranslation();
  const { state, dispatch } = useContext(Context);
  const [displayAdvancedFilters, setDisplayAdvancedFilters] = useState(false);
  const [checkedAdvancedFilters, setCheckedAdvancedFilters] = useState(0);
  const [filtersConfigured, setFiltersConfigured] = useState(false);
  const [currentStringifiedFilters, setCurrentStringifiedFilters] = useState("");
  const [requestedStringifiedFilters, setRequestedStringifiedFilters] = useState(null);
  const [isDeparmentsLoaded, setIsDepartmentsLoaded] = useState(false);
  const [isSchoolsLoaded, setIsSchoolsLoaded] = useState(false);
  const [shouldLoadFilters, setShouldLoadFilters] = useState(false);
  // The return request owns this mount even after its navigation flag is consumed.
  const [shouldSkipInitialPackageSearch] = useState(!!props.isRestoringList);

  const location = useLocation();
  const locationState: LocationState = location.state;
  const themeMode = ThemeStore.useThemeStore(state => state?.theme.mode);

  const filtersToDisplay = state?.filtersSelectors?.visibleFilter;
  const setFiltersToDisplay = (value: FiltersType) => {
    dispatch({ type: FiltersSelectorsTypes.SetFilterToDisplay, payload: value });
  };

  /**
   * These keys are used to trigger requests after any selector dependency
   * change. For example, when a `campus` is selected the selectable
   * `schools` have to be re-calculated, ensuring that the selectable `schools`
   * are actually related to the selected `campus`.
   */
  const campusSelectorKey = useMemo(
    () => state?.filters?.subject?.id ?? "",
    [state?.filters?.subject],
  );
  const schoolSelectorKey = useMemo(
    () => [state?.filters?.subject?.id ?? "", state?.filters?.campus?.id ?? ""].join(""),
    [state?.filters?.subject, state?.filters?.campus],
  );
  const departmentSelectorKey = useMemo(
    () =>
      [
        state?.filters?.subject?.id ?? "",
        state?.filters?.campus?.id ?? "",
        state?.filters?.school?.id ?? "",
      ].join(""),
    [state?.filters?.subject, state?.filters?.campus, state?.filters?.school],
  );

  useEffect(() => {
    setIsDepartmentsLoaded(false);
    setIsSchoolsLoaded(false);
  }, [state?.filters?.subject, state?.filters?.campus]);

  const updateFilter =
    (filter: BooleanFilterId) => (value: React.ChangeEvent<HTMLInputElement> | boolean) => {
      dispatch({
        type: Types.SetAdvancedFilter,
        payload: {
          filter,
          value:
            typeof value !== "boolean"
              ? Boolean((value as React.ChangeEvent<HTMLInputElement>)?.target?.checked)
              : value,
        },
      });
    };

  /**
   * After the any filter change, recalculate the number of selected filters (excluding `owned`).
   */
  useEffect(() => {
    const advancedFilters = [
      state?.filters?.availability,
      state?.filters?.enrollments,
      state?.filters?.state,
      state?.filters?.visibility,
    ];
    const countAdvancedFilters = advancedFilters
      .flatMap(filters => Object.values(filters ?? {}))
      .filter(value => value === true).length;
    const numberOfSelectedCourseTypes = Object.keys(state?.filters?.courseTypesById ?? {}).length;

    setCheckedAdvancedFilters(countAdvancedFilters + numberOfSelectedCourseTypes);
    setCurrentStringifiedFilters(stringifyFilters(state?.filters));
  }, [state?.filters]);

  /**
   * Set the `filtersSetted` variable, which determines if the button
   * `Mostrar resultados` should be `enabled` or `disabled`.
   */
  useEffect(() => {
    const { campus, school, department, subject } = state?.filters ?? {};
    const subjectSelected = subject != null;
    const campusSelected = campus != null;
    const othersSelected = school != null || department != null;
    const filtersByKeyConfigured =
      filtersToDisplay === FiltersType.KEY &&
      (subjectSelected || (campusSelected && othersSelected));
    const filtersByCrnConfigured =
      filtersToDisplay === FiltersType.CRN && Boolean(state?.filters?.selectedCrn);
    const filtersByPackageConfigured =
      filtersToDisplay === FiltersType.PACKAGE && !!state?.filters?.selectedPackage;

    setFiltersConfigured(
      filtersByCrnConfigured || filtersByKeyConfigured || filtersByPackageConfigured,
    );
  }, [state?.filters]);

  const isDisabledResultsButton = () => {
    if (!filtersConfigured) {
      return true;
    }

    const isKeyType = filtersToDisplay === FiltersType.KEY;

    if (isKeyType && currentStringifiedFilters === requestedStringifiedFilters) {
      return true;
    }

    if (isKeyType && (!isDeparmentsLoaded || !isSchoolsLoaded)) {
      return true;
    }

    return false;
  };

  const handleSelectPackageFilter = (item: Package) => {
    const selectableOption: SelectableOption<Package> = {
      id: item?.id,
      value: item?.id,
      label: item?.code,
      self: item,
    };

    dispatch({ type: FiltersSelectorsTypes.SetSelectedPackage, payload: selectableOption || null });
    dispatch({ type: Types.SelectPackage, payload: item?.id || null });
  };

  const handleSearchRequest = () => {
    props?.requestGroups?.({
      page: 1,
      avoidEditionsCleaning: false,
      usedFilter: filtersToDisplay,
    });
    setRequestedStringifiedFilters(stringifyFilters(state?.filters));
  };

  const handleCleanFilters = () => {
    dispatch({ type: FiltersSelectorsTypes.SetSelectedPackage, payload: null });
    dispatch({ type: Types.CleanFilters });
  };

  useEffect(() => {
    if (
      !shouldSkipInitialPackageSearch &&
      state?.filters?.selectedPackage &&
      state?.filtersSelectors?.selectedPackage?.id
    ) {
      handleSearchRequest();
    }

    if (locationState?.filter && locationState?.data && !shouldLoadFilters) {
      dispatch({ type: Types.CleanFilters });
      dispatch({ type: Types.SelectPackage, payload: locationState?.data?.id });
      dispatch({
        type: FiltersSelectorsTypes.SetSelectedPackage,
        payload: {
          id: locationState?.data?.id,
          value: locationState?.data?.id,
          label: locationState?.data?.code,
          self: locationState?.data,
        },
      });
      dispatch({
        type: FiltersSelectorsTypes.SetFilterToDisplay,
        payload: FiltersType.PACKAGE,
      });

      setShouldLoadFilters(true);
    }
  }, [shouldLoadFilters, shouldSkipInitialPackageSearch]);

  return (
    <section className={cx(styles.filtersCard, themeMode === "dark" && styles.filtersCard__dark)}>
      <header className={styles.filtersCard_header}>
        <Heading type="h3" className={styles.headerTitle}>
          {t("groups-manager.general-filters.title")}
        </Heading>
        <Switch
          className={styles.headerSwitch}
          label={t("groups-manager.general-filters.my-groups")}
          checked={state?.filters?.owned}
          onChange={() => updateFilter("owned")(!state?.filters?.owned)}
        />
      </header>

      <div className={styles.filtersCard_typeSelector}>
        <Heading type="h4" className={styles.typeTitle}>
          {t("groups-manager.general-filters.search-by.title")}
        </Heading>

        <RadioButton
          className={styles.typeSwitch}
          labelRight={t("groups-manager.general-filters.search-by.key.title")}
          checked={filtersToDisplay === FiltersType.KEY}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFiltersToDisplay(e?.currentTarget?.checked ? FiltersType.KEY : FiltersType.CRN);
            handleCleanFilters();
          }}
        />

        <RadioButton
          className={styles.typeSwitch}
          labelRight={t("groups-manager.general-filters.search-by.crn")}
          checked={filtersToDisplay === FiltersType.CRN}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFiltersToDisplay(e?.currentTarget?.checked ? FiltersType.CRN : FiltersType.KEY);
            handleCleanFilters();
          }}
        />

        <RadioButton
          className={styles.typeSwitch}
          labelRight={t("groups-manager.general-filters.search-by.package")}
          checked={filtersToDisplay === FiltersType.PACKAGE}
          onChange={() => {
            setFiltersToDisplay(FiltersType.PACKAGE);
            handleCleanFilters();
          }}
        />
      </div>

      <AnimatePresence mode="wait">
        {filtersToDisplay === FiltersType.KEY && (
          <motion.ul key={FiltersType.KEY} {...selectorsAnimationProps}>
            <li className={styles.filterWrapper}>
              <SubjectSelector />
              <CampusSelector key={campusSelectorKey} />
            </li>

            <li className={styles.filterSeparator}>
              <Icon name="link" color="neutral-50" />
            </li>

            <li className={styles.filterWrapper}>
              <SchoolSelector
                key={`school-${schoolSelectorKey}`}
                onLoading={isLoading => setIsSchoolsLoaded(!isLoading)}
              />
              <DepartmentSelector
                key={departmentSelectorKey}
                onLoading={isLoading => setIsDepartmentsLoaded(!isLoading)}
              />
            </li>
          </motion.ul>
        )}

        {filtersToDisplay === FiltersType.CRN && (
          <motion.ul key={FiltersType.CRN} {...selectorsAnimationProps}>
            <li className={cx(styles.filterWrapper, styles.filterWrapper_crn)}>
              <CrnSelector />
            </li>
          </motion.ul>
        )}

        {filtersToDisplay === FiltersType.PACKAGE && (
          <motion.ul key={FiltersType.PACKAGE} {...selectorsAnimationProps}>
            <li className={cx(styles.filterWrapper, styles.filterWrapper_package)}>
              <PackageSelector
                initialValue={state?.filtersSelectors?.selectedPackage}
                onSelectPackage={handleSelectPackageFilter}
              />
            </li>
          </motion.ul>
        )}
      </AnimatePresence>

      {/* Advanced filters */}
      <section className={styles.filtersCard_advancedFilters}>
        <div className={styles.filtersHeader}>
          <Link
            className={styles.filtersHeader_toggle}
            onClick={() => setDisplayAdvancedFilters(!displayAdvancedFilters)}
            iconLeft="filter"
            colored
          >
            {t("groups-manager.general-filters.aditional-filters.title")}
          </Link>
          <div className={styles.filtersHeader_separator}></div>
        </div>

        <motion.div
          layout
          layoutDependency={checkedAdvancedFilters}
          initial={{
            height: 0,
            opacity: 0,
            overflow: "hidden",
          }}
          animate={
            displayAdvancedFilters
              ? {
                  height: "auto",
                  opacity: 1,
                  overflow: "visible",
                }
              : {
                  height: 0,
                  opacity: 0,
                  overflow: "hidden",
                }
          }
          transition={{
            height: { duration: 0.25, ease: [0.4, 0, 0.2, 1] },
            opacity: { duration: 0.3, ease: "easeInOut" },
          }}
        >
          <ul
            className={cx(
              styles.filtersFields,
              !displayAdvancedFilters && styles.filtersFields__hide,
            )}
          >
            <li className={styles.advancedFilter}>
              <Heading type="h4" className={styles.advancedFilter_title}>
                {t("groups-manager.general-filters.aditional-filters.status.title")}
              </Heading>
              <Checkbox
                className={styles.advancedFilter_checkbox}
                labelRight={t("groups-manager.general-filters.aditional-filters.status.active")}
                checked={state?.filters?.state?.active}
                onChange={updateFilter("state.active")}
              />
              <Checkbox
                className={styles.advancedFilter_checkbox}
                labelRight={t("groups-manager.general-filters.aditional-filters.status.inactive")}
                checked={state?.filters?.state?.inactive}
                onChange={updateFilter("state.inactive")}
              />
            </li>

            <li className={cx(styles.advancedFilter, displayAdvancedFilters ? "" : styles.hide)}>
              <Heading type="h4" className={styles.advancedFilter_title}>
                {t("groups-manager.general-filters.aditional-filters.visibility.title")}
              </Heading>
              <Checkbox
                className={styles.advancedFilter_checkbox}
                labelRight={t(
                  "groups-manager.general-filters.aditional-filters.visibility.visible",
                )}
                checked={state?.filters?.visibility?.visible}
                onChange={updateFilter("visibility.visible")}
              />
              <Checkbox
                className={styles.advancedFilter_checkbox}
                labelRight={t(
                  "groups-manager.general-filters.aditional-filters.visibility.not-visible",
                )}
                checked={state?.filters?.visibility?.invisible}
                onChange={updateFilter("visibility.invisible")}
              />
            </li>

            <li className={cx(styles.advancedFilter, displayAdvancedFilters ? "" : styles.hide)}>
              <Heading type="h4" className={styles.advancedFilter_title}>
                {t("groups-manager.general-filters.aditional-filters.availability.title")}
              </Heading>
              <Checkbox
                className={styles.advancedFilter_checkbox}
                labelRight={t(
                  "groups-manager.general-filters.aditional-filters.availability.available",
                )}
                checked={state?.filters?.availability?.available}
                onChange={updateFilter("availability.available")}
              />
              <Checkbox
                className={styles.advancedFilter_checkbox}
                labelRight={t(
                  "groups-manager.general-filters.aditional-filters.availability.not-available",
                )}
                checked={state?.filters?.availability?.unavailable}
                onChange={updateFilter("availability.unavailable")}
              />
            </li>

            <li className={cx(styles.advancedFilter, displayAdvancedFilters ? "" : styles.hide)}>
              <Heading type="h4" className={styles.advancedFilter_title}>
                {t("groups-manager.general-filters.aditional-filters.enrolled.title")}
              </Heading>
              <Checkbox
                className={styles.advancedFilter_checkbox}
                labelRight={t(
                  "groups-manager.general-filters.aditional-filters.enrolled.with-enrolled",
                )}
                checked={state?.filters?.enrollments?.with}
                onChange={updateFilter("enrollments.with")}
              />
              <Checkbox
                className={styles.advancedFilter_checkbox}
                labelRight={t(
                  "groups-manager.general-filters.aditional-filters.enrolled.without-enrolled",
                )}
                checked={state?.filters?.enrollments?.without}
                onChange={updateFilter("enrollments.without")}
              />
            </li>

            <li
              className={cx(
                styles.advancedFilter,
                displayAdvancedFilters ? "" : styles.hide,
                styles.advancedFilter_selector,
              )}
            >
              <Heading type="h4" className={styles.advancedFilter_title}>
                {t("groups-manager.general-filters.aditional-filters.uf-selector.label")}
              </Heading>
              <CourseTypeSelector />
            </li>
          </ul>
        </motion.div>

        <div className={styles.filtersTags}>
          <Tag
            className={state?.filters?.state?.active ? "" : styles.hide}
            type="outlined"
            icon="close"
            onIconClick={() => updateFilter("state.active")(false)}
          >
            {t("groups-manager.general-filters.aditional-filters.status.active")}
          </Tag>
          <Tag
            className={state?.filters?.state?.inactive ? "" : styles.hide}
            type="outlined"
            icon="close"
            onIconClick={() => updateFilter("state.inactive")(false)}
          >
            {t("groups-manager.general-filters.aditional-filters.status.inactive")}
          </Tag>
          <Tag
            className={state?.filters?.visibility?.visible ? "" : styles.hide}
            type="outlined"
            icon="close"
            onIconClick={() => updateFilter("visibility.visible")(false)}
          >
            {t("groups-manager.general-filters.aditional-filters.visibility.visible")}
          </Tag>
          <Tag
            className={state?.filters?.visibility?.invisible ? "" : styles.hide}
            type="outlined"
            icon="close"
            onIconClick={() => updateFilter("visibility.invisible")(false)}
          >
            {t("groups-manager.general-filters.aditional-filters.visibility.not-visible")}
          </Tag>
          <Tag
            className={state?.filters?.availability?.available ? "" : styles.hide}
            type="outlined"
            icon="close"
            onIconClick={() => updateFilter("availability.available")(false)}
          >
            {t("groups-manager.general-filters.aditional-filters.availability.available")}
          </Tag>
          <Tag
            className={state?.filters?.availability?.unavailable ? "" : styles.hide}
            type="outlined"
            icon="close"
            onIconClick={() => updateFilter("availability.unavailable")(false)}
          >
            {t("groups-manager.general-filters.aditional-filters.availability.not-available")}
          </Tag>
          <Tag
            className={state?.filters?.enrollments?.with ? "" : styles.hide}
            type="outlined"
            icon="close"
            onIconClick={() => updateFilter("enrollments.with")(false)}
          >
            {t("groups-manager.general-filters.aditional-filters.enrolled.with-enrolled")}
          </Tag>
          <Tag
            className={state?.filters?.enrollments?.without ? "" : styles.hide}
            type="outlined"
            icon="close"
            onIconClick={() => updateFilter("enrollments.without")(false)}
          >
            {t("groups-manager.general-filters.aditional-filters.enrolled.without-enrolled")}
          </Tag>

          {Object.values(state?.filters?.courseTypesById ?? {}).map(courseType => (
            <Tag
              key={courseType?.id}
              type="outlined"
              icon="close"
              onIconClick={() =>
                dispatch({ type: Types.RemoveCourseTypeById, payload: courseType })
              }
            >
              {courseType?.name}
            </Tag>
          ))}
        </div>
      </section>

      {/* Buttons */}
      <section className={styles.filtersCard_actions}>
        <Button
          variant="secondary"
          disabled={
            !filtersConfigured && Object.keys(state?.filters?.courseTypesById ?? {}).length === 0
          }
          onClick={() => dispatch({ type: Types.CleanFilters })}
        >
          {t("groups-manager.general-filters.delete-selection")}
        </Button>
        <Button disabled={isDisabledResultsButton()} onClick={handleSearchRequest}>
          {t("groups-manager.general-filters.search-button")}
        </Button>
      </section>
    </section>
  );
};

export default Filters;
